// The decision-point elicitation card: one question, five probability chips, and the S-2 estimate
// revealed only afterwards.
//
// The ordering is the whole design. A forecast shown before the player commits is an anchor, and an
// anchored forecast is worthless as a measure of the player's own judgment — it measures how much they
// trust the card. So the estimate is fetched the moment the question appears (so there is no wait) and
// *held* until a chip is clicked. The card then shows both, and the debrief scores both.
//
// This is the model-free half of the Jev work: nothing here needs a model at all. With the toggle off
// it runs against the deterministic heuristic and the exercise is identical; with it on, the line under
// the answer reads "Jev, calibrated on 1,844 fights" instead of "heuristic". That is deliberate — the
// pattern is worth shipping either way, and the model is an implementation detail of one line of copy.
//
// Insertion into the live stage (one line, after `<TargetingBanner />` in `LiveStage.tsx`):
//
//     <ElicitCard />
//
// It is self-contained: it reads the store itself, positions itself absolutely, renders nothing when
// there is no question open, and takes no props.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../../state/store';
import { useElicit, PROB_CHIPS, type ElicitAnswer, type RecordedEstimate } from '../../state/elicit';
import { Chip } from '../cards/ui';
import { estimatorContextFromSnapshot, noteFirstSeen, type FirstSeenMap } from '../../ai/copContext';
import { getEstimator, RESERVE_WINDOW_MIN } from '../../ai/estimator';
import { calibrationStatus } from '../../ai/calibration';
import { getJevFlag } from '../../ai/jevFlag';
import { afterAsking, nextTrigger, EMPTY_TRIGGER_STATE, type ElicitTrigger, type TriggerState } from './triggers';
import type { Event } from '../../sim/types';

/**
 * Watches the event stream and returns the question that should be open, if any.
 *
 * Exported so the live stage can drive it explicitly if it ever wants to; `<ElicitCard />` calls it
 * itself, so the ordinary insertion is the one line above and nothing else.
 */
export function useElicitTriggers(events: readonly Event[]): {
  trigger: ElicitTrigger | null;
  dismiss: () => void;
  commit: () => void;
} {
  const tick = useStore((s) => s.snapshot?.tick ?? 0);
  const scenario = useStore((s) => s.scenario);
  const stage = useStore((s) => s.stage);
  const stateRef = useRef<TriggerState>(EMPTY_TRIGGER_STATE);
  const [trigger, setTrigger] = useState<ElicitTrigger | null>(null);

  const thirds = useMemo(() => {
    const graphics = scenario?.graphics ?? [];
    if (graphics.length === 0) return ['south', 'center', 'north'];
    // `corridorFrame` is cheap and pure; the names are all that is wanted here.
    const { names } = estimatorContextFromSnapshot(
      {
        tick: 0, units: [], picture: { contacts: [], reported: [] }, orders: [],
        net: { occupancy: 0, queueSec: 0 }, force: { strength: 0, fuel: 0, ammo: 0 },
        phase: '', ended: null,
      },
      { dials: useStore.getState().dials, scenario, graphics },
    ).frame;
    return [...names];
  }, [scenario]);

  // A new fight clears the record: the forecasts belong to the run they were made in.
  useEffect(() => {
    if (stage !== 'live') return;
    stateRef.current = EMPTY_TRIGGER_STATE;
    setTrigger(null);
  }, [stage]);

  useEffect(() => {
    if (trigger !== null) return; // one question at a time
    const next = nextTrigger(events, stateRef.current, { thirds, tick });
    if (next) setTrigger(next);
  }, [events, tick, thirds, trigger]);

  const close = useCallback(() => {
    setTrigger((current) => {
      if (current) {
        stateRef.current = afterAsking(stateRef.current, current, tick);
        useElicit.getState().markAsked(current.eventId);
      }
      return null;
    });
  }, [tick]);

  return { trigger, dismiss: close, commit: close };
}

/** Tracks, for the life of one fight, when each enemy was first reported. */
function useFirstSeen(): FirstSeenMap {
  const ref = useRef<FirstSeenMap>(new Map());
  const snapshot = useStore((s) => s.snapshot);
  useEffect(() => {
    if (snapshot) noteFirstSeen(ref.current, snapshot.picture.contacts, snapshot.tick);
  }, [snapshot]);
  return ref.current;
}

export function ElicitCard() {
  const targeting = useStore((s) => s.targeting);
  const events = useStore((s) => s.events);
  const snapshot = useStore((s) => s.snapshot);
  const scenario = useStore((s) => s.scenario);
  const dials = useStore((s) => s.dials);
  const meta = useStore((s) => s.meta);
  const firstSeen = useFirstSeen();
  const { trigger, dismiss, commit } = useElicitTriggers(events);

  const [committed, setCommitted] = useState<{ prob: number; choice?: string } | null>(null);
  const [estimate, setEstimate] = useState<RecordedEstimate | null>(null);
  const estimateRef = useRef<RecordedEstimate | null>(null);

  const calibration = calibrationStatus();

  // Fetch the estimate as soon as the question opens, and hold it. The player never sees it before
  // they commit; fetching early only means there is no wait once they do.
  useEffect(() => {
    estimateRef.current = null;
    setEstimate(null);
    setCommitted(null);
    if (!trigger || !snapshot || !scenario) return;
    let live = true;

    const ctx = estimatorContextFromSnapshot(snapshot, {
      dials, scenario, graphics: scenario.graphics, aoTitle: meta?.title, firstSeen,
    });
    const estimator = getEstimator(dials);

    void (async () => {
      const record = (value: string, probability: number, e: {
        confidence: number; provider: 'heuristic' | 'jev'; calibrated: boolean; latencyMs: number;
      }): RecordedEstimate => ({
        value,
        probability,
        confidence: e.confidence,
        provider: e.provider,
        calibrated: e.calibrated,
        latencyMs: e.latencyMs,
        calibratedOnRuns: calibration.runs,
      });

      if (trigger.question === 'reserveCommitSoon') {
        const e = await estimator.reserveCommitSoon(ctx);
        if (!live) return;
        estimateRef.current = record(e.value ? 'yes' : 'no', e.probabilities.yes ?? 0, e);
      } else {
        const e = await estimator.mainEffortThird(ctx);
        if (!live) return;
        estimateRef.current = record(e.value, e.probabilities[e.value] ?? 0, e);
      }
    })();

    return () => { live = false; };
    // `firstSeen` is a stable ref object; `calibration.runs` is a build constant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, snapshot === null, scenario, dials, meta]);

  const choose = (prob: number, choice?: string): void => {
    if (!trigger || !snapshot) return;
    const est = estimateRef.current ?? {
      value: trigger.question === 'reserveCommitSoon' ? 'no' : (trigger.options?.[1] ?? 'center'),
      probability: 0.5,
      confidence: 0,
      provider: 'heuristic' as const,
      calibrated: false,
      latencyMs: 0,
      calibratedOnRuns: calibration.runs,
    };
    const answer: ElicitAnswer = {
      t: snapshot.tick,
      question: trigger.question,
      prompt: trigger.prompt,
      playerChoice: choice,
      playerProb: prob,
      estimatorEstimate: est,
      triggerEventId: trigger.eventId,
    };
    useElicit.getState().record(answer);
    setCommitted({ prob, choice });
    setEstimate(est);
  };

  // A forecast must not intercept a map click needed to finish an order. Keep the question pending.
  if (!trigger || targeting) return null;

  const isChoice = trigger.question === 'mainEffortThird';
  const options = trigger.options ?? [];

  return (
    <div
      data-testid="elicit-card"
      className="fade-in absolute left-1/2 top-24 z-30 w-[340px] -translate-x-1/2 rounded-sm border bg-slate-900/95 p-3 backdrop-blur-sm"
      style={{ borderColor: 'var(--color-warn)' }}
    >
      <div className="flex items-baseline gap-2">
        <span
          className="mono text-[9px] uppercase tracking-[0.2em]"
          style={{ color: 'var(--color-warn)' }}
        >
          Your call
        </span>
        <span className="flex-1" />
        <button
          type="button"
          data-testid="elicit-skip"
          onClick={dismiss}
          className="mono text-[9px] uppercase tracking-[0.15em] text-fog hover:text-paper"
        >
          skip
        </button>
      </div>

      <p className="mt-1.5 text-[13px] leading-snug text-paper">{trigger.prompt}</p>
      <p className="mt-1 text-[10px] leading-snug text-fog">{trigger.because}</p>

      {committed === null ? (
        <div className="mt-2.5 space-y-2">
          {isChoice ? (
            options.map((option) => (
              <div key={option} className="flex items-center gap-1.5">
                <span className="mono w-14 shrink-0 text-[10px] uppercase tracking-[0.1em] text-fog">
                  {option}
                </span>
                {PROB_CHIPS.map((p) => (
                  <Chip
                    key={p}
                    onClick={() => choose(p, option)}
                    testId={`elicit-${option}-${Math.round(p * 100)}`}
                    title={`${Math.round(p * 100)}% chance the main effort is ${option}`}
                  >
                    {Math.round(p * 100)}%
                  </Chip>
                ))}
              </div>
            ))
          ) : (
            <div className="flex items-center gap-1.5">
              {PROB_CHIPS.map((p) => (
                <Chip
                  key={p}
                  onClick={() => choose(p)}
                  testId={`elicit-yes-${Math.round(p * 100)}`}
                  title={`${Math.round(p * 100)}% chance he commits within ${RESERVE_WINDOW_MIN} minutes`}
                >
                  {Math.round(p * 100)}%
                </Chip>
              ))}
            </div>
          )}
          <p className="text-[10px] leading-snug text-fog">
            Pick the probability you would actually bet on. The S-2 estimate is held back until you do.
          </p>
        </div>
      ) : (
        <div className="mt-2.5">
          <div className="mono text-[11px] text-paper" data-testid="elicit-yours">
            Yours: {Math.round(committed.prob * 100)}%
            {committed.choice ? ` · ${committed.choice}` : ''}
          </div>
          <div className="mono mt-1 text-[11px]" style={{ color: 'var(--color-friendly)' }} data-testid="elicit-estimate">
            {estimate
              ? `S-2 estimate: ${Math.round(estimate.probability * 100)}%`
                + `${estimate.value && isChoice ? ` · ${estimate.value}` : ''}`
                + ` · ${estimate.provider === 'jev' ? 'Jev' : 'heuristic'}`
                + `${estimate.calibrated ? `, calibrated on ${estimate.calibratedOnRuns.toLocaleString()} fights` : ''}`
              : 'S-2 estimate: still out'}
          </div>
          <p className="mt-1.5 text-[10px] leading-snug text-fog">
            Both go on the reliability strip in the debrief, scored against what the enemy actually did.
          </p>
          <div className="mt-2">
            <Chip onClick={commit} testId="elicit-close">close</Chip>
          </div>
        </div>
      )}
    </div>
  );
}
